import { SkeletonProvider, Tooltip } from '@protoss/ui';
import * as React from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { ProtectedRoute } from './components/ProtectedRoute';
import { PublicRoute } from './components/PublicRoute';
import { AppContextProvider } from './contexts/AppContext';
import { AuthProvider } from './contexts/AuthContext';
import { ChatSessionProvider } from './contexts/ChatSessionContext';
import { DirtyStateProvider } from './contexts/DirtyStateContext';
import { LayoutProvider } from './contexts/LayoutContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { useWorkspace, WorkspaceProvider } from './contexts/WorkspaceContext';
import { JobToaster } from './components/JobToaster';
import { ChatNavigationProvider } from './features/ai-chat/navigation/ChatNavigationContext';
import { AiRunTracePage } from './features/ai-traces/AiRunTracePage';
import { LiveMeetingNotificationProvider } from './features/live-meeting/components/LiveMeetingNotificationProvider';
import { AdministrationPage } from './pages/AdministrationPage';
import { BprEngagementPage } from './pages/BprEngagementPage';
import { BprEngagementsPage } from './pages/BprEngagementsPage';
import { BprFrameworkPage } from './pages/BprFrameworkPage';
import { CustomersPage } from './pages/CustomersPage';
import { DealMain } from './pages/DealMain';
import { DealWorkspace } from './pages/DealWorkspace';
import { DesktopDealPage, DesktopHomePage } from './pages/DesktopWorkspacePage';
import { DevLogsPage } from './pages/DevLogsPage';
import { HomePage } from './pages/HomePage';
import { LiveMeetingPage } from './pages/LiveMeetingPage';
import { MeetingsPage } from './pages/MeetingsPage';
import { NoModulePage } from './pages/NoModulePage';
import { NotFoundPage } from './pages/NotFoundPage';
import { ProductPage } from './pages/ProductPage';
import { ProductAdminPage } from './pages/ProductTemplatePage';
import { SowStudioPage } from './pages/SowStudioPage';
import { ProjectPage } from './pages/ProjectPage';
import { SalesCompaniesPage } from './pages/SalesCompaniesPage';
import { SalesDealsPage } from './pages/SalesDealsPage';
import { SettingsPage } from './pages/SettingsPage';
import { EmailActionPage } from './pages/auth/EmailActionPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { OAuthResultPage } from './pages/auth/OAuthResultPage';
import { OAuthConsentPage } from './pages/oauth/OAuthConsentPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { SetPasswordPage } from './pages/auth/SetPasswordPage';
import { SignInPage } from './pages/auth/SignInPage';
import { SignUpPage } from './pages/auth/SignUpPage';
import { VerifyEmailSentPage } from './pages/auth/VerifyEmailSentPage';

/**
 * Root layout - wraps all routes in AppContextProvider.
 * Lives inside the data router so useBlocker (and future data-router APIs)
 * work in any descendant component.
 */
function AppLayout() {
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Firebase email action links (mode + oobCode) land at the root URL when
  // the Firebase Console "Customize action URL" is set to the domain root
  // instead of /auth/action. Intercept and forward to the proper handler.
  // Skip the redirect if already on /auth/action to avoid an infinite loop.
  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');
  if (mode && oobCode && location.pathname !== '/auth/action') {
    return <Navigate to={`/auth/action?${searchParams.toString()}`} replace />;
  }

  return (
    <DirtyStateProvider>
      <AppContextProvider>
        <ChatNavigationProvider>
          <ChatSessionProvider>
            <LiveMeetingNotificationProvider>
              <Outlet />
            </LiveMeetingNotificationProvider>
          </ChatSessionProvider>
        </ChatNavigationProvider>
      </AppContextProvider>
    </DirtyStateProvider>
  );
}

/**
 * ModuleLanding - dispatches to the correct landing shell based on the active module's
 * landingExperience configuration. Keeps App.tsx free of module-specific branching.
 */
function ModuleLanding() {
  const { activeModule, modules } = useWorkspace();
  const effectiveModule = activeModule ?? modules[0] ?? null;
  if (effectiveModule?.landingExperience === 'org_tree') return <HomePage />;
  return <ProductPage />;
}

function ModuleGuard({ children }: { children: React.ReactNode }) {
  const { isLoadingModules, modules } = useWorkspace();

  if (!isLoadingModules && modules.length === 0) {
    return <NoModulePage />;
  }

  return <>{children}</>;
}

/**
 * SowStudioRoute - runtime gate for the full-page SOW Studio. Enabled per-workspace
 * (a features.sow_studio override, else the SOW_STUDIO_STAGE gate vs the workspace min
 * stage; resolved server-side onto workspace.sowStudioEnabled). Disabled workspaces are
 * redirected home; renders nothing while the workspace loads.
 */
function SowStudioRoute() {
  const { workspace, isLoadingWorkspace } = useWorkspace();
  if (isLoadingWorkspace) return null;
  if (!workspace?.sowStudioEnabled) return <Navigate to="/" replace />;
  return <SowStudioPage />;
}

/**
 * HomeRedirect - redirects '/' to the active module's defaultRoute.
 * Falls back to the first available module if no active module is set.
 * Renders nothing while modules are loading.
 */
function HomeRedirect() {
  const { isLoadingModules, activeModule, modules } = useWorkspace();
  const navigate = useNavigate();
  const target = activeModule ?? modules[0] ?? null;

  React.useEffect(() => {
    if (isLoadingModules) return;
    if (target) {
      void navigate(target.defaultRoute, { replace: true });
    }
  }, [isLoadingModules, target, navigate]);

  if (!isLoadingModules && target === null) {
    return <NoModulePage />;
  }

  return null;
}

const router = createBrowserRouter([
  {
    element: <AppLayout />,
    children: [
      /* Protected app routes */
      {
        path: '/',
        element: (
          <ProtectedRoute>
            <HomeRedirect />
          </ProtectedRoute>
        ),
      },
      {
        path: '/do',
        element: (
          <ProtectedRoute>
            <ModuleGuard>
              <ModuleLanding />
            </ModuleGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: '/do/domain/new',
        element: (
          <ProtectedRoute>
            <ModuleGuard>
              <ModuleLanding />
            </ModuleGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: '/do/domain/:domainUuid',
        element: (
          <ProtectedRoute>
            <ModuleGuard>
              <ModuleLanding />
            </ModuleGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: '/do/domain/:domainUuid/edit',
        element: (
          <ProtectedRoute>
            <ModuleGuard>
              <ModuleLanding />
            </ModuleGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: '/do/domain/:domainUuid/project/new',
        element: (
          <ProtectedRoute>
            <ModuleGuard>
              <ModuleLanding />
            </ModuleGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: '/do/project/new',
        element: (
          <ProtectedRoute>
            <ModuleGuard>
              <ModuleLanding />
            </ModuleGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: '/do/project/:projectUuid',
        element: (
          <ProtectedRoute>
            <ModuleGuard>
              <ProjectPage />
            </ModuleGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: '/do/project/:projectUuid/:section',
        element: (
          <ProtectedRoute>
            <ModuleGuard>
              <ProjectPage />
            </ModuleGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: '/do/project/:projectUuid/edit',
        element: (
          <ProtectedRoute>
            <ModuleGuard>
              <ModuleLanding />
            </ModuleGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: '/administration',
        element: (
          <ProtectedRoute>
            <AdministrationPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/administration/:section',
        element: (
          <ProtectedRoute>
            <AdministrationPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/administration/products/:productUuid',
        element: <Navigate to="categories" replace />,
      },
      {
        path: '/administration/products/:productUuid/:productAdminSection',
        element: (
          <ProtectedRoute>
            <ProductAdminPage />
          </ProtectedRoute>
        ),
      },
      {
        // Category hub: a selected deal category, optionally with a chosen artifact.
        path: '/administration/products/:productUuid/categories/:categoryKey',
        element: (
          <ProtectedRoute>
            <ProductAdminPage />
          </ProtectedRoute>
        ),
      },
      {
        // Full-page, de-chromed SOW Template Studio (gated per-workspace at runtime by
        // SowStudioRoute). Two extra path segments so it never collides with the
        // single-segment :artifact route.
        path: '/administration/products/:productUuid/categories/:categoryKey/sow/studio',
        element: (
          <ProtectedRoute>
            <SowStudioRoute />
          </ProtectedRoute>
        ),
      },
      {
        path: '/administration/products/:productUuid/categories/:categoryKey/:artifact',
        element: (
          <ProtectedRoute>
            <ProductAdminPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/ai/traces/:runId',
        element: (
          <ProtectedRoute>
            <AiRunTracePage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/po',
        element: (
          <ProtectedRoute>
            <ModuleGuard>
              <ProductPage />
            </ModuleGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: '/po/meetings',
        element: (
          <ProtectedRoute>
            <ModuleGuard>
              <MeetingsPage />
            </ModuleGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: '/po/customers',
        element: (
          <ProtectedRoute>
            <ModuleGuard>
              <CustomersPage />
            </ModuleGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: '/po/customers/create',
        element: (
          <ProtectedRoute>
            <ModuleGuard>
              <CustomersPage />
            </ModuleGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: '/po/customers/:customerUuid',
        element: (
          <ProtectedRoute>
            <ModuleGuard>
              <CustomersPage />
            </ModuleGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: '/po/customers/:customerUuid/edit',
        element: (
          <ProtectedRoute>
            <ModuleGuard>
              <CustomersPage />
            </ModuleGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: '/so',
        element: (
          <ProtectedRoute>
            <ModuleGuard>
              <DealWorkspace />
            </ModuleGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: '/so/deal/new',
        element: (
          <ProtectedRoute>
            <ModuleGuard>
              <DealWorkspace />
            </ModuleGuard>
          </ProtectedRoute>
        ),
      },
      {
        // Persistent deal shell: owns the right rail (chat/docs/props) + shared
        // deal data so toggling view ↔ edit (the nested children below) never
        // remounts/reloads the rail.
        path: '/so/deal/:dealUuid',
        element: (
          <ProtectedRoute>
            <ModuleGuard>
              <DealWorkspace />
            </ModuleGuard>
          </ProtectedRoute>
        ),
        children: [
          // Both routes render the SAME component so navigating view<->edit does
          // not remount the pane (the doc surface / SOW editor stays alive); the
          // component reads the `/edit` suffix to switch modes.
          { index: true, element: <DealMain /> },
          { path: 'edit', element: <DealMain /> },
        ],
      },
      {
        path: '/so/deals',
        element: (
          <ProtectedRoute>
            <ModuleGuard>
              <SalesDealsPage />
            </ModuleGuard>
          </ProtectedRoute>
        ),
      },
      {
        // Desktop workspace (COS-TBD desktop shell): hosted by the coSow desktop app's
        // Workspace window. Dark, de-chromed, read-only; not linked from any menu.
        path: '/so/desktop',
        element: (
          <ProtectedRoute>
            <ModuleGuard>
              <DesktopHomePage />
            </ModuleGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: '/so/desktop/deals/:dealUuid',
        element: (
          <ProtectedRoute>
            <ModuleGuard>
              <DesktopDealPage />
            </ModuleGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: '/so/bpr',
        element: (
          <ProtectedRoute>
            <ModuleGuard>
              <BprEngagementsPage />
            </ModuleGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: '/so/bpr/engagements/:engagementUuid',
        element: (
          <ProtectedRoute>
            <ModuleGuard>
              <BprEngagementPage />
            </ModuleGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: '/so/bpr/framework',
        element: (
          <ProtectedRoute>
            <ModuleGuard>
              <BprFrameworkPage />
            </ModuleGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: '/so/companies',
        element: (
          <ProtectedRoute>
            <ModuleGuard>
              <SalesCompaniesPage />
            </ModuleGuard>
          </ProtectedRoute>
        ),
      },

      /* User settings - module-agnostic, accessible to all authenticated users */
      {
        path: '/settings',
        element: (
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/settings/:section',
        element: (
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        ),
      },

      /* Live meeting full-page route - module-scoped (e.g. /so/meeting/live) */
      {
        path: '/:urlToken/meeting/live',
        element: (
          <ProtectedRoute>
            <ModuleGuard>
              <LiveMeetingPage />
            </ModuleGuard>
          </ProtectedRoute>
        ),
      },

      /* Public auth routes - redirect to home if already authenticated */
      {
        path: '/signin',
        element: (
          <PublicRoute>
            <SignInPage />
          </PublicRoute>
        ),
      },
      {
        path: '/signup',
        element: (
          <PublicRoute>
            <SignUpPage />
          </PublicRoute>
        ),
      },
      {
        path: '/forgot-password',
        element: (
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        ),
      },
      {
        path: '/reset-password',
        element: (
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        ),
      },
      { path: '/auth/set-password', element: <SetPasswordPage /> },

      /* Unguarded routes - must work for both authenticated and unauthenticated users */
      { path: '/auth/action', element: <EmailActionPage /> },
      { path: '/verify-email-sent', element: <VerifyEmailSentPage /> },
      { path: '/oauth/result', element: <OAuthResultPage /> },
      /* MCP connector sign-in (COS-24): signs in on the page itself - see the component */
      { path: '/oauth/consent', element: <OAuthConsentPage /> },

      /* Dev-only tooling - no auth guard, not linked from any nav */
      ...(import.meta.env.DEV ? [{ path: '/dev/logs', element: <DevLogsPage /> }] : []),

      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);

export function App() {
  return (
    <AuthProvider>
      <WorkspaceProvider>
        <NotificationsProvider>
          <LayoutProvider>
            <Tooltip.Provider delayDuration={300} skipDelayDuration={200}>
              <SkeletonProvider loading={false}>
                <RouterProvider router={router} future={{ v7_startTransition: true }} />
                <JobToaster />
              </SkeletonProvider>
            </Tooltip.Provider>
          </LayoutProvider>
        </NotificationsProvider>
      </WorkspaceProvider>
    </AuthProvider>
  );
}
